import React, { useEffect, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Trash2 } from "lucide-react";

export default function AchievementsPanel() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ period: "weekly", week_of: "", student_name: "", award_type: "", award_type_zh: "", note: "" });
  const [saving, setSaving] = useState(false);

  async function load() {
    try { const { data } = await api.get("/achievements?limit=200"); setItems(data); } catch { /* ignore */ }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function submit(e) {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...draft };
      if (payload.period !== "weekly") payload.week_of = null;
      else if (!payload.week_of) delete payload.week_of;
      await api.post("/achievements", payload);
      toast.success("Achievement posted");
      setDraft({ period: draft.period, week_of: "", student_name: "", award_type: "", award_type_zh: "", note: "" });
      load();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setSaving(false); }
  }

  async function del(id) {
    if (!window.confirm("Remove this achievement?")) return;
    try { await api.delete(`/achievements/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  return (
    <div data-testid="admin-achievements-panel" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <form onSubmit={submit} className="lg:col-span-5 bg-white border border-[hsl(var(--border))] rounded-2xl p-6 space-y-4 h-fit" data-testid="admin-ach-form">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Post a new award</h3>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Type</Label>
          <Select value={draft.period} onValueChange={(v)=>setDraft({...draft, period: v})}>
            <SelectTrigger data-testid="admin-ach-period" className="mt-2"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly" data-testid="admin-ach-period-weekly">Weekly award</SelectItem>
              <SelectItem value="special" data-testid="admin-ach-period-special">Special honour</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {draft.period === "weekly" && (
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Week of (Monday) — optional</Label>
            <Input type="date" value={draft.week_of} onChange={(e)=>setDraft({...draft, week_of: e.target.value})} data-testid="admin-ach-week" className="mt-2" />
          </div>
        )}
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Student name</Label>
          <Input required value={draft.student_name} onChange={(e)=>setDraft({...draft, student_name: e.target.value})} data-testid="admin-ach-student" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Award type (English)</Label>
          <Input required value={draft.award_type} onChange={(e)=>setDraft({...draft, award_type: e.target.value})} placeholder="e.g. Most Improved Reader" data-testid="admin-ach-award" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Award type (中文)</Label>
          <Input value={draft.award_type_zh} onChange={(e)=>setDraft({...draft, award_type_zh: e.target.value})} placeholder="例如：阅读进步奖" data-testid="admin-ach-award-zh" className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Note (optional)</Label>
          <Textarea rows={2} value={draft.note} onChange={(e)=>setDraft({...draft, note: e.target.value})} data-testid="admin-ach-note" className="mt-2" />
        </div>
        <Button type="submit" disabled={saving} data-testid="admin-ach-submit" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">{saving ? "…" : "Post award"}</Button>
      </form>

      <div className="lg:col-span-7">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))] mb-3">Posted awards · {items.length}</h3>
        <ul className="divide-y divide-[hsl(var(--border))] bg-white border border-[hsl(var(--border))] rounded-2xl max-h-[70vh] overflow-y-auto">
          {items.map((a) => (
            <li key={a.id} data-testid={`admin-ach-row-${a.id}`} className="p-4 flex items-start gap-4 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
                  <span className={a.period === "weekly" ? "text-[hsl(var(--secondary))]" : "text-[hsl(var(--primary))]"}>
                    {a.period === "weekly" ? "WEEKLY" : "SPECIAL"}
                  </span>
                  <span className="text-[hsl(var(--muted-foreground))]">{a.week_of || format(parseISO(a.created_at), "MMM dd")}</span>
                </div>
                <div className="font-display text-lg tracking-tight mt-1">{a.student_name}</div>
                <div className="text-sm text-[hsl(var(--primary))]">{a.award_type}{a.award_type_zh ? ` · ${a.award_type_zh}` : ""}</div>
                {a.note && <div className="text-xs text-[hsl(var(--muted-foreground))] italic mt-1">&quot;{a.note}&quot;</div>}
              </div>
              <Button variant="ghost" size="sm" onClick={()=>del(a.id)} data-testid={`admin-ach-delete-${a.id}`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
          {items.length === 0 && <li className="p-8 text-center text-[hsl(var(--muted-foreground))]">No awards yet.</li>}
        </ul>
      </div>
    </div>
  );
}
